import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import ReactTable from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, ButtonGroup } from "react-bootstrap";
import { MenuProvider } from "react-contexify";
import ConfirmModal from "components/common/confirm-modal";
import ReactTableFilterSelect from "components/custom/forms/react-table-filter-select";
import filterCaseInsensitive from "components/custom/forms/react-table-case-insensitive";
import ListContextMenu from "components/menu/master-files/planogram-maintenance/list-context-menu";
import {
    getCategories,
    getEntryTypes,
    getPlanogramList,
    planogramCopyById,
    deletePlanogramById
} from "api/menu/master-files/planogram-maintenance/actions";
import {
    openCustomModal,
    closeCustomModal
} from "components/custom/modals";

const EditPlanogramModal = React.lazy(() =>
    import("components/menu/master-files/planogram-maintenance/edit-planogram-modal"));

class ReportBuilder extends React.Component {
    dateFormat = "YYYY-MM-DD";

    contextMenuId = "planogram-context";

    constructor(props) {
        super(props);

        this.state = {};

        this.getColumns = this.getColumns.bind(this);
        this.handleEdit = this.handleEdit.bind(this);
        this.handleCopy = this.handleCopy.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
        this.handleConfigure = this.handleConfigure.bind(this);

        this.props.getCategories();
        this.props.getEntryTypes();
        this.props.getPlanogramList();
    }

    /*
    planogramId: 2
    typeId: 124
    */
    getColumns = () => [
        {
            Header: "Planogram ID",
            accessor: "planogramId",
            headerClassName: "d-none",
            className: "d-none",
            width: 0,
            Cell: ({ original }) => (original.planogramId),
        },
        {
            Header: "Code",
            accessor: "planogramCode",
        },
        {
            Header: "Category",
            accessor: "category",
            Filter: ({
                         filter,
                         onChange
                     }) => {
                const options = {
                    "": "Show All"
                };
                this.props.categories.forEach(item => {
                    options[item.value] = item.value;
                });
                return (
                    <ReactTableFilterSelect
                        currentValue={filter ? filter.value : ""}
                        onChange={value => onChange(value)}
                        optionsObject={options}
                        style={{
                            width: "100%",
                            height: "100%"
                        }}
                    />
                );
            },
        },
        {
            Header: "Type",
            accessor: "planogramType",
            Filter: ({
                         filter,
                         onChange
                     }) => {
                const options = {
                    "": "Show All"
                };
                this.props.entryTypes.forEach(item => {
                    options[item.value] = item.value;
                });
                return (
                    <ReactTableFilterSelect
                        currentValue={filter ? filter.value : ""}
                        onChange={value => onChange(value)}
                        optionsObject={options}
                        style={{
                            width: "100%",
                            height: "100%"
                        }}
                    />
                );
            },
        },
        {
            Header: "Comments",
            accessor: "comments",
        },
        {
            Header: "# Of Columns",
            accessor: "numberOfColumns",
            width: 100
        },
        {
            Header: "# Of Rows",
            accessor: "numberOfRows",
            width: 80
        },
        {
            Header: "# Of Header Rows",
            accessor: "numberOfHeaderRows",
            width: 120
        },
        {
            Header: "Data Rows Added",
            accessor: "dataRowsCount",
        },
        {
            Header: "Layout Cells Added",
            accessor: "layoutCellsCount",
        },
        {
            Header: "Active?",
            id: "inactive",
            accessor: original => (original.inactive ? "No" : "Yes"),
            Cell: ({ original }) => (original.inactive ? "No" : "Yes"),
            Filter: ({
                         filter,
                         onChange
                     }) => (
                <ReactTableFilterSelect
                    currentValue={filter ? filter.value : ""}
                    onChange={value => onChange(value)}
                    style={{
                        width: "100%",
                        height: "100%"
                    }}
                    optionsObject={{
                        "": "Show All",
                        YES: "Yes",
                        NO: "No"
                    }}
                />
            ),
            width: 100,
        },
        {
            Header: "Actions",
            id: "actions",
            className: "d-flex justify-content-center align-items-center",
            filterable: false,
            width: 120,
            Cell: ({ original }) => (
                <div>
                    <Button
                        variant="primary mr-2"
                        size="sm"
                        onClick={() => this.handleConfigure(original.planogramId)}
                    >
                        <FontAwesomeIcon icon="cog"/>
                    </Button>

                    <Button
                        variant="secondary mr-2"
                        size="sm"
                        onClick={() => this.handleCopy(original.planogramId)}
                    >
                        <FontAwesomeIcon icon="copy"/>
                    </Button>

                    <ButtonGroup>
                        <Button
                            variant="warning"
                            size="sm"
                            onClick={() => this.handleEdit(original.planogramId)}
                        >
                            <FontAwesomeIcon icon="pen"/>
                        </Button>
                        <Button
                            variant="danger"
                            size="sm"
                            onClick={() => this.handleDelete(original.planogramId)}
                        >
                            <FontAwesomeIcon icon="times"/>
                        </Button>
                    </ButtonGroup>
                </div>
            )
        }
    ];

    handleCopy = (planogramId) => {
        this.props.planogramCopyById(planogramId);
    };

    handleDelete = (planogramId) => {
        const modalObj = {
            modalSettings: {
                size: "sm",
                width: 300
            },
            header: false,
            children: (
                <React.Suspense
                    fallback={<div className="m-2 text-center font-weight-bold">Loading...</div>}>
                    <ConfirmModal
                        title={(
                            <div>
                                Removing planogram.<br/>
                                Are you sure?
                            </div>
                        )}
                        onCancel={() => this.props.closeCustomModal(modalObj)}
                        onConfirm={() => {
                            this.props.deletePlanogramById(planogramId);
                            this.props.closeCustomModal(modalObj);
                        }}
                    />
                </React.Suspense>
            ),
            handleClose: () => this.props.closeCustomModal(modalObj)
        };
        this.props.openCustomModal(modalObj);
    };

    handleConfigure = (planogramId) => {
        this.props.history.push(`/planogramMaintenance/${planogramId}`);
    };

    handleEdit = (planogramId = false) => {
        const modalObj = {
            modalSettings: {
                size: "md",
            },
            header: `${planogramId ? "Edit" : "New"} Planogram`,
            children: (
                <React.Suspense
                    fallback={<div className="m-2 text-center font-weight-bold">Loading...</div>}>
                    <EditPlanogramModal
                        planogramId={planogramId}
                        onCancel={() => this.props.closeCustomModal(modalObj)}
                        onSave={() => {
                            this.props.closeCustomModal(modalObj);
                            this.props.getPlanogramList();
                        }}
                    />
                </React.Suspense>
            ),
            handleClose: () => this.props.closeCustomModal(modalObj)
        };
        this.props.openCustomModal(modalObj);
    };

    render() {
        const {
            loading,
            planogramList
        } = this.props;

        return (
            <div className="dashboard">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h3 className="mb-0">
                        Planogram Maintenance
                    </h3>

                    <button
                        className="btn btn-success"
                        onClick={() => this.handleEdit()}
                    >
                        Add New Planogram
                    </button>
                </div>

                <ReactTable
                    data={planogramList}
                    columns={this.getColumns()}
                    headerClassName="table-header"
                    showPagination={true}
                    defaultPageSize={20}
                    pageSizeOptions={[10, 20, 50, 100]}
                    resizable
                    className="-striped -highlight planogram-table"
                    loading={loading}
                    filterable={true}
                    defaultFiltered={[
                        {
                            id: "inactive",
                            value: "YES"
                        }
                    ]}
                    defaultSorted={[{
                        id: "completedDate",
                        desc: false
                    }]}
                    defaultFilterMethod={filterCaseInsensitive}
                    getNoDataProps={() => ({
                        style: {
                            zIndex: 0
                        }
                    })}
                    TrComponent={(props) => {
                        if (props.children[0]
                            && props.children[0].props
                            && props.children[0].props.children
                            && props.children[0].props.children.props
                            && props.children[0].props.children.props.original
                            && props.children[0].props.children.props.original.planogramId
                        ) {
                            const { planogramId } =
                                props.children[0].props.children.props.original;
                            return (
                                <MenuProvider
                                    className="rt-tr"
                                    id={this.contextMenuId}
                                    data={{
                                        planogramId,
                                        handleConfigure: () => this.handleConfigure(planogramId),
                                        handleCopy: () => this.handleCopy(planogramId),
                                        handleEdit: () => this.handleEdit(planogramId),
                                        handleDelete: () => this.handleDelete(planogramId),
                                    }}
                                >
                                    <div className={`rt-tr ${props.className}`} style={props.style}
                                         role="row">
                                        {props.children}
                                    </div>
                                </MenuProvider>
                            );
                        } else {
                            return (
                                <div className={`rt-tr ${props.className}`} style={props.style}
                                     role="row">
                                    {props.children}
                                </div>
                            );
                        }
                    }}
                    style={{
                        height: "calc(100vh - 140px)",
                    }}
                />

                {planogramList.length && <ListContextMenu elementId={this.contextMenuId}/>}
            </div>
        );
    }
}

const mapStateToProps = state => ({
    loading: state.planogramMaintenance.loading,
    categories: state.planogramMaintenance.categories,
    entryTypes: state.planogramMaintenance.entryTypes,
    planogramList: state.planogramMaintenance.planogramList,
});

const mapStateToDispatch = dispatch => ({
    openCustomModal: bindActionCreators(openCustomModal, dispatch),
    closeCustomModal: bindActionCreators(closeCustomModal, dispatch),

    getPlanogramList: bindActionCreators(getPlanogramList, dispatch),
    getCategories: bindActionCreators(getCategories, dispatch),
    getEntryTypes: bindActionCreators(getEntryTypes, dispatch),
    planogramCopyById: bindActionCreators(planogramCopyById, dispatch),
    deletePlanogramById: bindActionCreators(deletePlanogramById, dispatch),
});

export default connect(
    mapStateToProps,
    mapStateToDispatch
)(ReportBuilder);
